// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/CompetitorsResearchCard.tsx
// ADAPTED (plan K0/K3): TP's card (Radar tile, "Building {brand}'s comparable cohort", status badge, step rows),
// driven only by what the BFF reports: the research job's competitive stage and GET /competitors. TP's scripted
// says, candidate and inspiration counts, "Reading the live ads of every brand we follow" and the 1.8s hypotheses
// delay have no source here and are not ported. The empty and failed lines are the transcript's (a log line is
// announced; this card is not a live region), so the card carries the state word and the steps only.
import { Radar } from 'lucide-react';

import { Card } from '@/components/ui/shadcn/card';
import type { GetCompetitorsResponse, SessionSnapshot, StepState } from '@/contracts/onboarding-session';
import { cn } from '@/lib/utils';

import { allFromCatalog, CI_JOB_COMPETITOR_LANDSCAPE, CI_STAGE_COMPETITIVE, COMPETITORS_CARD } from '../copy';
import type { DataState } from '../state/buildTranscript';
import { isTerminalState } from '../state/jobState';
import { JobBadge, type JobBadgeStatus } from './JobBadge';
import { STEP_STATUS_TEXT, StepStatusIcon } from './StepStatusIcon';

/** The research job's state for the competitive stage, or null when the BFF has not listed it (no workflow yet). */
export function competitiveStageState(snapshot: SessionSnapshot | null): StepState | null {
  const progress = snapshot?.researchProgress;
  const stage = progress?.stages?.find((s) => s.name === CI_STAGE_COMPETITIVE);
  if (stage) return stage.state;
  return progress?.jobs?.find((j) => j.name === CI_JOB_COMPETITOR_LANDSCAPE)?.state ?? null;
}

const RANK: Record<StepState, number> = { queued: 0, running: 1, done: 2, partial: 2, unavailable: 2, skipped: 2 };

/** The one that is further along: a terminal state beats running beats queued. Equal ranks keep the first. */
function furthest(a: StepState, b: StepState): StepState {
  return RANK[b] > RANK[a] ? b : a;
}

export interface CompetitorsResearchView {
  badge: JobBadgeStatus;
  /** The matching step: always shown. */
  matching: StepState;
  /** Says "our catalog" only when every shortlisted brand is from CI's catalog (never claimed before there is a shortlist). */
  matchingLabel: string;
  /** "Ranking the closest brands": null until a shortlist exists (never shown as queued before then). */
  ranking: StepState | null;
}

/**
 * The card's state, from the data only. `competitors` is GET /competitors (what the shortlist says), `snapshot`
 * the research job (what CI's competitive stage says); nothing here is a timer.
 *  - read failed: `unavailable`; the matching step is `unavailable` too.
 *  - shortlist present: matching is done, ranking is done once the research ended and running before; the badge
 *    is done, or partial when the research ended partial.
 *  - no shortlist and the research ended: `empty` (neutral), or `unavailable` when the research failed.
 *  - otherwise queued or running, by the furthest of the stage and the shortlist's status.
 */
export function competitorsResearchView(
  snapshot: SessionSnapshot | null,
  competitors: DataState<GetCompetitorsResponse> | undefined,
): CompetitorsResearchView {
  const stage = competitiveStageState(snapshot);
  if (competitors?.state === 'error') return { badge: 'unavailable', matching: 'unavailable', matchingLabel: COMPETITORS_CARD.matching, ranking: null };

  const read = competitors?.state === 'ready' ? competitors.data : null;
  const status: StepState = read ? (stage ? furthest(read.status, stage) : read.status) : (stage ?? 'queued');

  if (read && read.competitors.length > 0) {
    const ended = isTerminalState(read.status);
    return {
      badge: !ended ? 'running' : read.status === 'partial' ? 'partial' : 'done',
      matching: 'done',
      matchingLabel: allFromCatalog(read.competitors) ? COMPETITORS_CARD.matchingCatalog : COMPETITORS_CARD.matching,
      ranking: ended ? 'done' : 'running',
    };
  }
  if (isTerminalState(status)) {
    return {
      badge: status === 'unavailable' ? 'unavailable' : 'empty',
      matching: status === 'skipped' ? 'skipped' : status,
      matchingLabel: COMPETITORS_CARD.matching,
      ranking: null,
    };
  }
  return { badge: status === 'running' ? 'running' : 'queued', matching: status, matchingLabel: COMPETITORS_CARD.matching, ranking: null };
}

export interface CompetitorsResearchCardProps {
  brand: string;
  snapshot: SessionSnapshot | null;
  competitors: DataState<GetCompetitorsResponse> | undefined;
}

const ROW = 'flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none';

function Step({ id, label, status }: { id: string; label: string; status: StepState }) {
  return (
    <div className={ROW} data-step-id={id} data-status={status}>
      <StepStatusIcon status={status} />
      <span className={cn('min-w-0 flex-1 truncate text-body-sm', status === 'running' ? 'onb-shimmer' : 'text-foreground')}>{label}</span>
      <span className="sr-only">{`(${STEP_STATUS_TEXT[status]})`}</span>
    </div>
  );
}

/** Scout's competitor research in the conversation: one job card that updates in place until the shortlist is ready. */
export function CompetitorsResearchCard({ brand, snapshot, competitors }: CompetitorsResearchCardProps) {
  const view = competitorsResearchView(snapshot, competitors);
  return (
    <Card
      role="region"
      aria-label="Competitor research"
      data-testid="competitors-research-card"
      data-state={view.badge}
      className="w-full overflow-hidden"
    >
      <header className="flex items-center gap-2.5 border-b border-ds-hairline px-3 py-2.5">
        <span aria-hidden className="flex size-6 shrink-0 items-center justify-center rounded-sm bg-ds-ink-a05 text-ds-text-2">
          <Radar className="size-3.5" />
        </span>
        <h3 className="min-w-0 flex-1 truncate text-heading-sm text-foreground">{`Building ${brand}'s comparable cohort`}</h3>
        <JobBadge status={view.badge} data-testid="competitors-badge" />
      </header>
      <div className="flex flex-col gap-2 px-3 py-3">
        <Step id="matching" label={view.matchingLabel} status={view.matching} />
        {view.ranking ? <Step id="ranking" label={COMPETITORS_CARD.ranking} status={view.ranking} /> : null}
      </div>
    </Card>
  );
}
